/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';
import { merge, extend } from 'lodash';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Commands } from 'bridge-commands/commands';
import {
    JsEventsCommonData,
    UserConsentInfo,
    DataCollectionConsent,
    EventMetadata,
    UnassignedStates
} from '../models/common-data.models';
import { JsEventsService } from '../services/js-events.service';
import { TelemetryEvent } from './telemetry.models';

@Injectable({
    providedIn: 'root'
})
export class TelemetryService {
    private logger: Logger;

    private clientVersion = 'undefined';
    private currentScreen = '';
    private deviceId = 'undefined';
    private isUIRefreshed = false;
    private launchUiEventSent = false;
    private sessionId = 'undefined';
    private sync = false;
    private userId = 'undefined';
    private externalUserId = 'undefined';
    private idpId = 'undefined';
    private deviceOS = null;
    private deviceOSVersion = null;
    private deviceType = null;
    private deviceModel = null;
    private deviceMake = null;
    private clientType = null;
    private clientVariant = null;
    private integrationId = null;
    private browserType = null;
    private productName = null;
    private productVersion = null;
    private cpuArchitecture = null;
    private isTelemetryDisabled = false;
    private defaultConsentListener$ = new ReplaySubject<boolean>(1);
    private deviceConsentListener$ = new ReplaySubject<boolean>(1);
    private commonDataListener$ = new ReplaySubject<boolean>(1);
    private validUserIdResetListener$ = new ReplaySubject<boolean>(1);
    private consentUpdatedPromise: Promise<DataCollectionConsent> = new Promise(resolve => {
        resolve(null);
    });

    constructor(
        private loggingService: LoggingService,
        private bridgeService: BridgeService,
        private jsEventsService: JsEventsService
    ) {
        this.logger = this.loggingService.getLogger('telemetryService');
        this.getUIRefreshState();
        this.sessionId = this.generateSessionId();
    }

    public updateTelemetrySessionId(sessionId) {
        this.sessionId = sessionId;
    }

    // TODO evaluate if this is the right place for it
    private logEvaluatorCallback = (jsonObj: object) => true;

    initialize(isTelemetryDisabled: boolean) {
        this.isTelemetryDisabled = isTelemetryDisabled;
        this.jsEventsService.setOverrideToolTelemetryState(this.isTelemetryDisabled);
    }

    getCommonData$(): Observable<JsEventsCommonData> {
        return this.jsEventsService.getCommonData$();
    }

    setLaunchUiEventSent(eventSent: boolean): void {
        this.launchUiEventSent = eventSent;
        this.logger.info('Launch UI event sent: ', this.launchUiEventSent);
    }

    getLaunchUiEventSent(): boolean {
        return this.launchUiEventSent;
    }

    setLoggingEvaluator(evaluatorCallback: (jsonObj: object) => boolean) {
        this.logEvaluatorCallback = evaluatorCallback;
    }

    getUIRefreshState(): void {
        this.logger.info('Getting  UI Refreshed state');
        this.bridgeService.executeCommand(Commands.Common.Applicationinfo.GetIsUIRefreshed).subscribe(
            refreshed => {
                this.isUIRefreshed = refreshed;
                this.logger.info('Refreshed state', this.isUIRefreshed);
            },
            () => {
                this.logger.error('Failed to get UI Refreshed state');
            }
        );
    }

    private generateSessionId(): string {
        let d = Date.now();
        // This is based on UUID generation logic
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c: string) {
            /* eslint-disable no-bitwise */
            const r = ((d + Math.random() * 16) % 16) | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            /* eslint-enable no-bitwise */
        });
        return uuid;
    }

    get telemetrySessionId() {
        return this.sessionId;
    }

    setEventsCommonData(
        commonData: JsEventsCommonData,
        resetUserId: boolean = false,
        resetIntegrationId: boolean = false
    ): void {
        this.logger.info('telemetry sessionId is ', this.sessionId);
        if (!this.deviceId && commonData && commonData.deviceId) {
            this.logger.info('telemetry deviceId', commonData.deviceId);
        }
        if ((resetUserId && commonData && commonData.userId) || (commonData && commonData.externalUserId)) {
            this.logger.info('telemetry userId ', commonData.userId);
            this.logger.info('telemetry externalUserId ', commonData.externalUserId);
            this.logger.info('telemetry idpId ', commonData.idpId);
        }

        this.deviceId = commonData && commonData.deviceId ? commonData.deviceId : this.deviceId;
        this.clientVersion = (commonData && commonData.clientVer) || this.clientVersion;

        if (resetUserId) {
            this.userId = commonData.userId || 'undefined';
            this.externalUserId = commonData.externalUserId || 'undefined';
            this.idpId = commonData.idpId || 'undefined';
        }
        this.deviceOS = this.deviceOS || commonData.deviceOS;
        this.deviceOSVersion = this.deviceOSVersion || commonData.deviceOSVersion;
        this.deviceType = this.deviceType || commonData.deviceType;
        this.deviceModel = this.deviceModel || commonData.deviceModel;
        this.deviceMake = this.deviceMake || commonData.deviceMake;
        this.clientType = this.clientType || commonData.clientType;
        this.clientVariant = this.clientVariant || commonData.clientVariant;
        this.integrationId = this.integrationId || commonData.integrationId;
        this.browserType = this.browserType || commonData.browserType;
        this.productName = this.productName || commonData.productName;
        this.productVersion = this.productVersion || commonData.productVersion;
        this.cpuArchitecture = this.cpuArchitecture || commonData.cpuArchitecture;

        if (resetIntegrationId) {
            this.integrationId = commonData.integrationId || 'undefined';
        }
        const logCommonData: JsEventsCommonData = {
            clientVer: this.clientVersion,
            sessionId: this.sessionId,
            deviceId: this.deviceId,
            userId: this.userId,
            externalUserId: this.externalUserId,
            idpId: this.idpId,
            deviceOS: this.deviceOS,
            deviceOSVersion: this.deviceOSVersion,
            deviceType: this.deviceType,
            deviceModel: this.deviceModel,
            deviceMake: this.deviceMake,
            clientType: this.clientType,
            clientVariant: this.clientVariant,
            integrationId: this.integrationId,
            browserType: this.browserType,
            productName: this.productName,
            productVersion: this.productVersion,
            cpuArchitecture: this.cpuArchitecture
        };
        this.logger.info('Setting common events data: ', logCommonData);

        this.consentUpdatedPromise = this.jsEventsService
            .setEventsCommonData({
                clientVer: this.clientVersion,
                sessionId: this.sessionId,
                deviceId: this.deviceId,
                userId: this.userId,
                externalUserId: this.externalUserId,
                idpId: this.idpId,
                deviceOS: this.deviceOS || UnassignedStates.Undefined,
                deviceOSVersion: this.deviceOSVersion || UnassignedStates.Undefined,
                deviceType: this.deviceType || UnassignedStates.Undefined,
                deviceModel: this.deviceModel || UnassignedStates.Undefined,
                deviceMake: this.deviceMake || UnassignedStates.Undefined,
                clientType: this.clientType || UnassignedStates.Undefined,
                clientVariant: this.clientVariant || UnassignedStates.Undefined,
                integrationId: this.integrationId || UnassignedStates.Undefined,
                browserType: this.browserType || UnassignedStates.Undefined,
                productName: this.productName || UnassignedStates.Undefined,
                productVersion: this.productVersion || UnassignedStates.Undefined,
                cpuArchitecture: this.cpuArchitecture || UnassignedStates.Undefined
            })
            .toPromise();

        if (this.userId && this.deviceId && this.deviceId !== UnassignedStates.Undefined) {
            this.commonDataListener$.next(true);
        }

        const userIdHasSet = resetUserId && this.userId && this.userId !== UnassignedStates.Undefined;
        this.validUserIdResetListener$.next(userIdHasSet);
    }

    setScreen(screen: string): void {
        this.currentScreen = screen;
    }

    getScreen(): string {
        return this.currentScreen;
    }

    changeSync(status: boolean): void {
        this.sync = status;
    }

    setDefaultClientConsent(paramDefaultConsent: DataCollectionConsent): void {
        this.logger.event('Set client consent: ', paramDefaultConsent);
        this.jsEventsService.setDefaultConsent(paramDefaultConsent);
        this.defaultConsentListener$.next(true);
    }

    setDeviceConsent(deviceConsent: DataCollectionConsent): void {
        this.logger.event('Set device consent: ', deviceConsent);
        this.jsEventsService.setDeviceConsent(deviceConsent);
        this.defaultConsentListener$.next(true);
        this.deviceConsentListener$.next(true);
    }

    setUserConsent(userConsents: UserConsentInfo[]): void {
        this.logger.event('Set user consent: ', userConsents);
        this.jsEventsService.syncUserConsentInfo(userConsents);
    }

    getDefaultClientConsentObserable(): Observable<boolean> {
        return this.defaultConsentListener$.pipe(first());
    }

    getDeviceConsentObservable(): Observable<boolean> {
        return this.deviceConsentListener$.pipe(first());
    }

    getCommonDataObservable(): Observable<boolean> {
        return this.commonDataListener$.pipe(first());
    }

    getValidUserIdResetObservable(): Observable<boolean> {
        return this.validUserIdResetListener$.asObservable();
    }

    setServerConfig(serverUrl: string): void {
        this.jsEventsService.updateServer(serverUrl);
    }

    push<T>(eventInfo: TelemetryEvent<T>, startTime?: number, eventMetadata?: EventMetadata) {
        if (this.isUIRefreshed || this.isTelemetryDisabled) {
            this.logger.info('Disabled telemetry', eventInfo.name);
            return;
        }

        if (eventInfo) {
            eventMetadata = merge({}, eventMetadata);
            eventMetadata = extend(eventMetadata, { appExit: this.sync });
            // For events which deal with duration measurement, startTime and endTime are used to
            // calculate the total time taken.
            if (startTime && eventInfo.parameters.hasOwnProperty('totalMs')) {
                const endTime = Date.now();
                const durationMs = Math.round(endTime - startTime);
                this.logger.info(
                    'Started ',
                    eventInfo.name,
                    'at ',
                    startTime,
                    'and ended at ',
                    endTime,
                    'Time elapsed(ms)',
                    durationMs
                );
                eventInfo.parameters = merge(eventInfo.parameters, { totalMs: durationMs });
            }
            if (this.logEvaluatorCallback(eventInfo)) {
                this.logger.event('Event detail: ', eventInfo);
            }
            this.consentUpdatedPromise.then(() => {
                this.logger.info('Sending event ' + eventInfo.name);
                this.jsEventsService.sendEventDetail(eventInfo, eventMetadata);
            });
        } else {
            this.logger.error('Empty event.');
        }
    }

    pushWithPromise<T>(
        eventInfo: TelemetryEvent<T>,
        startTime?: number,
        eventMetadata?: EventMetadata
    ): Promise<string> {
        if (this.isUIRefreshed || this.isTelemetryDisabled) {
            this.logger.info('Disabled telemetry', eventInfo.name);
            return new Promise((resolve, reject) => {
                resolve('Disabled telemetry');
            });
        }

        if (eventInfo) {
            eventMetadata = merge({}, eventMetadata);
            eventMetadata = extend(eventMetadata, { appExit: this.sync });
            // For events which deal with duration measurement, startTime and endTime are used to
            // calculate the total time taken.
            if (startTime && eventInfo.parameters.hasOwnProperty('totalMs')) {
                const endTime = Date.now();
                const durationMs = Math.round(endTime - startTime);
                this.logger.info(
                    'Started ',
                    eventInfo.name,
                    'at ',
                    startTime,
                    'and ended at ',
                    endTime,
                    'Time elapsed(ms)',
                    durationMs
                );
                eventInfo.parameters = merge(eventInfo.parameters, { totalMs: durationMs });
            }
            if (this.logEvaluatorCallback(eventInfo)) {
                this.logger.event('Event detail: ', eventInfo);
            }
            return new Promise(resolve => {
                this.consentUpdatedPromise
                    .then(() => this.jsEventsService.sendEventDetailWithPromise(eventInfo, eventMetadata))
                    .then(returnCode => {
                        resolve(returnCode);
                    });
            });
        } else {
            this.logger.error('Empty event.');
            return new Promise((resolve, reject) => {
                reject('Empty event.');
            });
        }
    }
}
